import { useCallback, useMemo } from "react";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { getBodyTree, getEffectiveWorldBodies } from "../../stores/sceneActions/bodyUtils";
import { useSceneStore } from "../../stores/sceneStore";
import { logger } from "../../utils/logger";
import { EMPTY_OBJECT } from "../scene/sceneConstants";

export function computeBodiesStructureSignature(nodes, jointMap = {}) {
  const normalizeJointRefs = (jointRefs) =>
    (Array.isArray(jointRefs) ? jointRefs : [])
      .map((entry) => {
        if (typeof entry === "string") return entry;
        if (entry && typeof entry === "object" && typeof entry.uuid === "string") return entry.uuid;
        return "";
      })
      .filter(Boolean)
      .sort();

  const walk = (arr) =>
    (arr || [])
      .map((node) => {
        if (!node?.uuid) {
          logger.error("[Scene] Body node missing UUID in structure signature.");
        }
        const jointUuids = normalizeJointRefs(node?.joints);
        return {
          key: node?.uuid || "",
          joints: jointUuids.map((uuid) => `${uuid}:${jointMap[uuid]?.kind ?? ""}`),
          children: node?.children ? walk(node.children) : [],
        };
      })
      .sort((a, b) => (a.key > b.key ? 1 : a.key < b.key ? -1 : 0));
  try {
    return JSON.stringify(walk(nodes || []));
  } catch {
    return Math.random().toString();
  }
}

export const computeFileMapSignature = (fileMap) => {
  if (!fileMap) return "";
  try {
    return Object.keys(fileMap).sort().join(",");
  } catch {
    return "";
  }
};

export const getSortedFileKeys = (fileMap) => {
  if (!fileMap) return [];
  try {
    return Object.keys(fileMap).sort();
  } catch {
    return [];
  }
};

export const getRootUuids = (nodes = []) =>
  (nodes || []).map((node) => (node && node.uuid ? node.uuid : null)).filter(Boolean);

export function useSceneStructureSnapshot({ bodies, safeBodies, worldBodies }) {
  const effectiveWorldBodies = useMemo(
    () => getEffectiveWorldBodies({ bodies, worldBodies }),
    [bodies, worldBodies]
  );

  const bodyTree = useMemo(
    () => getBodyTree({ bodies, worldBodies: effectiveWorldBodies }),
    [bodies, effectiveWorldBodies]
  );

  const childrenMapByUuid = useMemo(() => {
    const map = {};
    Object.values(bodies ?? EMPTY_OBJECT).forEach((body) => {
      if (body && body.uuid && Array.isArray(body.children)) {
        map[body.uuid] = body.children
          .map((child) => {
            if (typeof child === "string") return child;
            if (child && typeof child === "object" && typeof child.uuid === "string")
              return child.uuid;
            return null;
          })
          .filter((childUuid) => typeof childUuid === "string" && safeBodies[childUuid]);
      }
    });
    return map;
  }, [bodies, safeBodies]);

  const collectDescendantsByUuid = useCallback(
    (rootUuid) => {
      if (!rootUuid) return [];
      const out = [];
      const stack = [...(childrenMapByUuid[rootUuid] || [])];
      while (stack.length) {
        const cur = stack.pop();
        if (!cur) continue;
        out.push(cur);
        const kids = childrenMapByUuid[cur] || [];
        if (kids.length) stack.push(...kids);
      }
      return out;
    },
    [childrenMapByUuid]
  );

  const rebuildStructureSnapshot = useStoreWithEqualityFn(
    useSceneStore,
    (state) => {
      const bodiesNow = state?.bodies || EMPTY_OBJECT;
      const worldBodiesNow = state?.worldBodies || [];
      const jointsNow = state?.joints || EMPTY_OBJECT;
      const sitesNow = state?.sites || EMPTY_OBJECT;
      const effectiveWorldBodiesNow = getEffectiveWorldBodies({
        bodies: bodiesNow,
        worldBodies: worldBodiesNow,
      });
      const bodyTreeNow = getBodyTree({
        bodies: bodiesNow,
        worldBodies: effectiveWorldBodiesNow,
      });
      const structureSig = computeBodiesStructureSignature(bodyTreeNow, jointsNow);
      const hasWorldLevelSites = Object.values(sitesNow).some((site) => {
        const siteUuid = site?.uuid;
        if (!siteUuid) return false;
        return !Object.values(bodiesNow).some(
          (body) =>
            Array.isArray(body?.sites) && body.sites.some((id) => String(id) === String(siteUuid))
        );
      });
      return {
        bodyTree: bodyTreeNow,
        structureSig,
        hasWorldLevelSites,
      };
    },
    (prev, next) =>
      prev?.structureSig === next?.structureSig &&
      prev?.hasWorldLevelSites === next?.hasWorldLevelSites
  );

  return {
    bodyTree,
    collectDescendantsByUuid,
    computeBodiesStructureSignature,
    computeFileMapSignature,
    effectiveWorldBodies,
    getRootUuids,
    getSortedFileKeys,
    hasWorldLevelSitesForRebuild: rebuildStructureSnapshot?.hasWorldLevelSites === true,
    rebuildBodyTree: rebuildStructureSnapshot?.bodyTree || [],
    rebuildStructureSig: rebuildStructureSnapshot?.structureSig || "",
  };
}
